<template>
  <div class="gift-card-page container" ref="frame">
    <div class="product-section" aria-hidden="true">
      <img 
        :src="smallProductImage" 
        :alt="selectedProductTitle" 
        class="product-image-skeleton no_cache"
      />
      <img 
        :src="backgroundProductImage" 
        :alt="selectedProductTitle" 
        class="product-image no_cache"
        @load="handleImageLoad"
      />
      <div class="overlay">
        <img class="gift-card-logo" :src="theme.images.wf_logo" role="presentation">
        <p>A gift for you!<br>You have received a {{ selectedVariant ? selectedVariant.title : 'Gift Card' }} White Fox e-Gift card</p>
        <div class="rounded-image-container" :class="{ sparkle: !selectedProduct.tags.includes('no_sparkle') }" ref="card">
          <img 
            :src="smallProductImage" 
            :alt="selectedProductTitle" 
            class="rounded-image-skeleton no_cache"
          />
          <img
            :src="selectedProductImage"
            :alt="selectedProductTitle"
            class="rounded-image no_cache"
            @load="handleImageLoad"
            ref="roundedContainer"
          />
        </div>
        <div class="message">
          <p v-if="showRecipientName">Hey, {{ recipientName }}</p>
          <p>{{ recipientMessage }}</p>
          <p v-if="showSenderName">From {{ senderName }}</p>
        </div>
      </div>
    </div>
    <div class="gift-card-selection">
      <form class="gift-card-form" @submit.prevent="handleSubmit">
        <div role="alert" aria-live="polite" v-if="formError" class="form-error">
          {{ formError }}
        </div>
        <div role="alert" aria-live="polite" v-if="formSuccess" class="form-success">
          {{ formSuccess }}
        </div>
        <div class="gift-card-header">
          <h1 aria-label="E-Gift Card">E-Gift Card</h1>
          <p>{{ giftCardIntro }}</p>
        </div>
        <div class="rounded-image-container-mob" :class="{ sparkle: !selectedProduct.tags.includes('no_sparkle') }">
          <img 
            :src="smallProductImage" 
            :alt="selectedProductTitle" 
            class="rounded-image-skeleton no_cache"
          />
          <img
            :src="selectedProductImage"
            :alt="selectedProductTitle"
            class="rounded-image no_cache"
            @load="handleImageLoad"
            ref="roundedContainer"
          />
        </div>
        <fieldset class="gift-card-steps">
          <legend class="gift-card-steps-header">1. Choose a design</legend>
          <div v-if="products && products.length" class="gift-card-designs">
            <label
              v-for="product in products.filter(p => p.title !== 'Gift Card')"
              :key="product.id"
              class="product-label"
              :class="{ active: selectedProduct && selectedProduct.id === product.id }"
              :aria-selected="selectedProduct && selectedProduct.id === product.id"
            >
              <input
                type="radio"
                name="product_id"
                :value="product.id"
                class="hidden-radio"
                :checked="selectedProduct && selectedProduct.id === product.id"
                @change="selectProduct(product)"
                required
                :aria-label="`Select gift card design: ${product.title}`"
              />
              <img
                :src="product.featured_image"
                :alt="`Gift card design: ${product.title}`"
                class="gift-card-image"
              />
            </label>
          </div>
        </fieldset>
        <fieldset class="gift-card-steps">
          <legend class="gift-card-steps-header">
            2. Choose an amount 
            <small v-if="convertedPrice">{{ convertedPrice }}</small>
          </legend>
          <div v-if="products && selectedProduct" class="gift-card-denominations">
            <label
              v-for="variant in selectedProduct.variants"
              :key="variant.id"
              class="gift-card-denomination-label"
              :class="{ active: selectedVariant && selectedVariant.id === variant.id }"
            >
              <input
                type="radio"
                name="variant_id"
                id="price"
                :value="variant.id"
                class="hidden-radio"
                :checked="selectedVariant && selectedVariant.id === variant.id"
                @change="selectVariant(variant)"
                required
                aria-label="Select gift card amount:"
              />
              <span class="gift-card-denomination-amount">{{formatTitle(variant.title) }}</span>
            </label>
          </div>
        </fieldset>

        <!-- /////RECIPIENT FORM///// -->
        <fieldset class="gift-card-steps">
          <div class="gift-card-steps-header-container">
            <legend class="gift-card-steps-header">
              3. Personalise your card <small>(optional)</small>
            </legend>
          </div>
          <div class="personalise-accordion-content">
            <div class="recipient-fields__field">
              <label for="recipient-email">Their Email</label>
              <input type="email" id="recipient-email" v-model="recipientEmail" placeholder="Recipient's Email">
            </div>
            <div class="recipient-names-row">
              <div class="recipient-fields__field">
                <label for="recipient-name">Their Name</label>
                <input type="text" id="recipient-name" v-model="recipientName" placeholder="Their Name">
              </div>
              <div class="recipient-fields__field">
                <label for="sender-name">Your Name</label>
                <input type="text" id="sender-name" v-model="senderName" placeholder="Your Name">
              </div>
            </div>
            <div class="recipient-fields__field">
              <label for="recipient-message">Message</label>
              <textarea id="recipient-message" v-model="recipientMessage" placeholder="Personal Message" maxlength="200" rows="4"></textarea>
            </div>
          </div>
        </fieldset>
        <button class="btn btn-primary product__add-to-cart d-block w-100" type="submit">+ Add to Bag </button>
        <div v-if="defaultProduct" class="gift-card-product-description" v-html="defaultProduct.description"></div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "gift_card_selector",
  props: {
    products: Array,
    giftCardIntro: {
      type: String,
      default: 'Looking for the perfect present? Give them (or yourself) a White Fox gift card.'
    },
    currencySymbol: {
      type: String,
      default: "$",
    },
    currency: {
      type: String,
      default: "AUD",
    },
  },
  
  data() {
    return {
      selectedProduct: this.products[0] || null,
      defaultProduct: this.products.find(p => p.title === 'Gift Card') || this.products[0] || null,
      selectedVariant: null,
      recipientEmail: '',
      recipientName: '',
      senderName: '',
      recipientMessage: '',
      selectedAmount: "$0", 
      frameRect: null,
      // whiteFoxLogo: window.whiteFoxLogo
    };
  },

  mounted() {
    this.updateFrameRect();
    window.addEventListener("resize", this.updateFrameRect);

    const frame = this.$refs.frame;
    frame.addEventListener("mouseenter", this.handleMouseEnter);
    frame.addEventListener("mouseleave", this.handleMouseLeave);
  },

  computed: {
    selectedProductImage() {
      return this.selectedProduct ? this.selectedProduct.featured_image : '';
    },
    backgroundProductImage() {
      return this.selectedProduct && this.selectedProduct.images && this.selectedProduct.images[1]
        ? this.selectedProduct.images[1] 
        : this.selectedProduct.featured_image || '';
    },
    selectedProductTitle() {
      return this.selectedProduct ? this.selectedProduct.title : 'Gift Card';
    },
    smallProductImage() {
      return `${this.selectedProductImage}&width=1`;
    },
    showRecipientName() {
      return !!this.recipientName;
    },
    showSenderName() {
      return !!this.senderName;
    },
    convertedPrice() {
      if (!this.selectedVariant) return null;

      const match = this.selectedVariant.title.match(/([\d,.]+)/);
      if (!match) return null;

      const denominationPriceInCents = parseFloat(match[1].replace(',', '')) * 100;
      if (denominationPriceInCents !== this.selectedVariant.price) {
        return `${this.currencySymbol}${(this.selectedVariant.price / 100).toFixed(2)} ${this.currency}`;
      }
      return null;
    }
  },
  
  created() {
    if (this.products && this.products.length > 0) {
      this.loadFromQuery();
      if (this.selectedProduct && this.selectedProduct.variants.length > 0) {
        this.selectedVariant = this.selectedProduct.variants[0];
        this.selectedAmount = this.formatMoney(this.selectedVariant.price);
      }
    } else {
      console.log("Products array not loaded yet; waiting for watcher.");
    }
  },

  watch: {
    products(newProducts) {
      if (newProducts.length > 0) {
        this.loadFromQuery();
      }
    }
  },
  
  methods: {
    updateFrameRect() {
      this.frameRect = this.$refs.frame.getBoundingClientRect();
    },
    
    handleMouseEnter() {
      this.$refs.frame.addEventListener("mousemove", this.handleMouseMove);
    },
        
    handleMouseLeave() {
      this.$refs.frame.removeEventListener("mousemove", this.handleMouseMove);
      this.resetCardStyle();
    },
    
    handleMouseMove(e) {
      if (!this.frameRect) return;

      const { x, y, width, height } = this.frameRect;
      const left = e.clientX - x;
      const top = e.clientY - y;
      const centerX = left - width / 2;
      const centerY = top - height / 2;

      const rotationX = centerY / 50;
      const rotationY = -centerX / 50;
      this.$refs.card.style.transform = `rotate3d(${rotationX}, ${rotationY}, 0, 14deg)`;
    },
    
    resetCardStyle() {
      this.$refs.card.style.boxShadow = "0 4px 10px rgba(0, 0, 0, 0.1)";
      this.$refs.card.style.transform = "";
    },

    handleImageLoad(event) {
      event.target.classList.add('_loaded');
    },
    
    selectProduct(product) {
      const previousPrice = this.selectedVariant ? this.selectedVariant.price : null;
      this.selectedProduct = product;
      const matchingVariant = product.variants.find(v => v.price === previousPrice);

      if (matchingVariant) {
        this.selectedVariant = matchingVariant;
      } else {
        this.selectedVariant = product.variants[0];
      }
      this.selectedAmount = this.formatMoney(this.selectedVariant.price);
      this.updateQuery();
    },

    selectVariant(variant) {
      this.selectedVariant = variant;
      this.selectedAmount = this.formatMoney(variant.price);
      this.updateQuery();
    },
    
    formatMoney(price) {
      return `${(price / 100).toFixed(2).replace(/\.00$/, '')}`;
    },

    formatTitle(title) {
      return title.replace(/\.00$/, '');
    },

    updateQuery() {
      const params = new URLSearchParams(window.location.search);
      if (this.selectedProduct) {
        params.set('product_id', this.selectedProduct.id);
      }
      if (this.selectedVariant) {
        params.set('variant_id', this.selectedVariant.id);
      }
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', newUrl);
    },

    loadFromQuery() {
      const params = new URLSearchParams(window.location.search);
      const productId = params.get('product_id');
      const variantId = params.get('variant_id');
      const product = this.products.find(p => String(p.id) === String(productId));

      if (product) {
        this.selectedProduct = product;

        let variant = product.variants.find(v => String(v.id) === String(variantId));
        if (!variant && product.variants.length > 0) {
          variant = product.variants[0];
        }

        if (variantId) {
          const variant = product.variants.find(v => String(v.id) === String(variantId));

          if (variant) {
            this.selectedVariant = variant;
          } else {
          }
        } else {
          console.log("Variant ID not provided in URL");
        }
      } else {
        console.log("Product not found for ID:", productId);
      }
    },

    async handleSubmit() {
      if (!this.selectedVariant) {
        alert('Please select a variant.');
        return;
      }

      const variantID = this.selectedVariant.id;

      const properties = {
        '_dismiss_set': true,
        ...this.recipientEmail ? {
          '__shopify_send_gift_card_to_recipient': 'on',
          'Recipient email': this.recipientEmail,
          ...this.recipientName ? { 'Recipient name': this.recipientName } : {},
          ...this.senderName ? { 'Sender name': this.senderName } : {},
          ...this.recipientMessage ? { 'Message': this.recipientMessage } : {},
        } : {},
      };

      if (this.selectedProduct) {
        trackGiftCardEvent(this.selectedProduct.title || 'Unknown Gift Card');
      }

      try {
        await window.cartUtils.addItem(variantID, 1, { properties });

        this.toggleSidecart(true);
      } catch (error) {
        console.error('Failed to add item to cart:', error);
      }
    },

    toggleSidecart(show = true) {
      const drawerNowOpen = window.drawerFunctions.toggleDrawer('drawer_sidecart', show);
      if (drawerNowOpen) {
        window.analytics.cartView();
        if (typeof window.closeAllMegamenus === 'function') {
          window.closeAllMegamenus();
        }
      }
    }
  }
};

function trackGiftCardEvent(selectedProductTitle) {
  try {
    window.dispatchEvent(new CustomEvent('analytics:gtag', {
      detail: {
        event: 'gc_add_to_cart',
        params: {
          event_category: 'Gift Cards',
          event_label: selectedProductTitle,
          event_action: 'add_to_cart',
        }
      }
    }));
  } catch (err) {
    console.error('Failed to dispatch analytics event for gift card', err);
  }
}
</script>
